import { Injectable } from '@angular/core';
import { Observable, concat, defer, from, of } from 'rxjs';
import { catchError, map, tap, toArray } from 'rxjs/operators';
import {
  IBatchRow,
  IBatchSaveContext,
  IPreparedRow,
  ItemBatchSaveService,
  MAX_SAVES_IN_FLIGHT,
  RowSaveOutcome,
  failedOutcome
} from '../item-write/item-batch-save.service';
import { ItemWriteService } from '../item-write/item-write.service';
import { fanOut } from '../item-write/rules/item-write-fanout';
import { ItemWithPendingOffers } from '../../../shared/interfaces/offer/Offer';
import { ItemEditStore } from './item-edit.store';
import { ItemListingService } from './item-listing.service';

/** What the pre-flight left: the bodies that may go out, and the rows refused before
 *  anything was sent. The offers check asks about `ready` and nothing else.
 *
 *  `checkFailed` are the rows whose offers check failed (HUBX-8511): they leave `ready` and
 *  are never sent, and the save reports them failed, as a refused PUT is. */
export interface IListingSavePlan {
  ready: IPreparedRow[];
  invalid: RowSaveOutcome[];
  checkFailed?: RowSaveOutcome[];
}

/** What the offers check answered (HUBX-8511): the plan to commit, without the rows whose
 *  check failed, and the items the cancellation warning lists, in page order. */
export interface IListingOffersCheck {
  plan: IListingSavePlan;
  affected: ItemWithPendingOffers[];
}

/** One row's answer to the offers check. */
type OffersAnswer =
  | { kind: 'none' }
  | { kind: 'affected'; item: ItemWithPendingOffers }
  | { kind: 'failed'; outcome: RowSaveOutcome };

/** How a save ended, by row id. `kept` are the rows whose offers the user kept in the
 *  cancellation warning: never sent, and left edited (HUBX-8511). `refillFailed` means the
 *  saved rows kept the provisional baseline `ItemEditStore.apply` gave them. */
export interface IListingSaveSummary {
  saved: string[];
  failed: string[];
  invalid: string[];
  kept: string[];
  refillFailed: boolean;
}

/** One `row` per row as it settles (already folded into the store), then one `done`. */
export type ListingSaveEvent =
  | { kind: 'row'; outcome: RowSaveOutcome }
  | { kind: 'done'; summary: IListingSaveSummary };

/**
 * The listing's multi-row save (HUBX-8479), joining the engine of HUBX-8477 to the page's
 * edited-row store:
 *
 *   prepare -> affectedOffers (one v2 check per row, HUBX-8511) -> the user decides -> commit
 *
 * The check is split out because the decision between the two halves is the user's: the
 * page opens the cancellation warning on what `affectedOffers` returns and passes back the ids
 * whose offer the user keeps. Nothing here renders; the per-row marks are HUBX-8478.
 *
 * After the fan-out it refills ONLY the saved rows by id (HUBX-8488) — never `GET /items`,
 * which would replace the page and lose the marks. The counters (HUBX-8473) and the filter
 * options (HUBX-8487) are the page's to refetch on `done`, since their triggers are page state.
 */
@Injectable({
  providedIn: 'root'
})
export class ItemListingSaveService {
  constructor(
    private batchSave: ItemBatchSaveService,
    private listing: ItemListingService,
    private itemWriteService: ItemWriteService
  ) { }

  /** Step 1: the per-row pre-flight over the store's edited rows, in page order. Nothing is
   *  sent. `ids` narrows it to those rows — a row's own Save icon (HUBX-8480) — and leaves
   *  every other edit where it is. */
  prepare(store: ItemEditStore, ctx: IBatchSaveContext, ids?: readonly string[]): IListingSavePlan {
    return this.batchSave.prepare(store.batch(ids), ctx);
  }

  /** Whether the pre-flight refuses one edited row: prepare's verdict on it, which is what
   *  Update All waits on (HUBX-8466) - it waits on exactly the rows it would not send. */
  refuses(row: IBatchRow, ctx: IBatchSaveContext): boolean {
    return this.batchSave.prepare([row], ctx).invalid.length > 0;
  }

  /**
   * Step 2: the offers check, `POST /items/{id}/affected-offers` (HUBX-8502), one per ready
   * row with the very body its PUT will carry — run as the PUTs are: the same `fanOut`, the
   * same key (`itemId`) and `MAX_SAVES_IN_FLIGHT` (HUBX-8511).
   *
   * Per row, as the PUT: a 200 puts the item in the warning; a 204 (null) leaves the row to
   * be sent; any failure is that row's alone (`failedOutcome`) — it leaves `ready` for
   * `checkFailed` and the other rows go on. The answers arrive in completion order and are
   * put back in page order. No ready row, no request.
   */
  affectedOffers(plan: IListingSavePlan): Observable<IListingOffersCheck> {
    if (!plan.ready.length) { return of({ plan, affected: [] }); }
    return fanOut(plan.ready, r => r.itemId, r => this.check(r), MAX_SAVES_IN_FLIGHT).pipe(
      toArray(),
      map(answers => {
        const byId = new Map(answers.map(({ item, result }) => [item.id, result]));
        const ready: IPreparedRow[] = [];
        const checkFailed: RowSaveOutcome[] = [];
        const affected: ItemWithPendingOffers[] = [];
        for (const row of plan.ready) {
          const answer = byId.get(row.id);
          if (answer?.kind === 'failed') {
            checkFailed.push(answer.outcome);
            continue;
          }
          ready.push(row);
          if (answer?.kind === 'affected') {
            affected.push(answer.item);
          }
        }
        return { plan: { ...plan, ready, checkFailed }, affected };
      })
    );
  }

  /** One row's check, with every failure turned into a value so the fan-out keeps going. */
  private check(row: IPreparedRow): Observable<OffersAnswer> {
    return this.itemWriteService.affectedOffers(row.id, row.request).pipe(
      map((item): OffersAnswer => item ? { kind: 'affected', item } : { kind: 'none' }),
      catchError((err: unknown) => of<OffersAnswer>({ kind: 'failed', outcome: failedOutcome(row.id, err) }))
    );
  }

  /**
   * Step 3: send the rows whose offer the user did not keep, fold every outcome into the
   * store, then refill the saved rows.
   *
   * - `invalid` rows, then `checkFailed` rows, are reported first; they were never sent and
   *   stay edited.
   * - Kept rows are neither sent nor reverted (HUBX-8511): they stay edited, with their
   *   tokens, and are listed in `kept`.
   * - `saved` rows leave the store's Map with their rotated token; `failed` rows stay, tokens
   *   unrotated, for a retry.
   * - A failed refill does not fail the save — the PUTs are committed. The rows keep the
   *   provisional baseline and `refillFailed` says so.
   *
   * Cold: nothing is sent until subscribed.
   */
  commit(store: ItemEditStore, plan: IListingSavePlan, keptIds: readonly string[]): Observable<ListingSaveEvent> {
    return defer(() => {
      const kept = new Set(keptIds);
      const toSend = plan.ready.filter(r => !kept.has(r.id));

      const summary: IListingSaveSummary = {
        saved: [],
        failed: [],
        invalid: [],
        kept: plan.ready.filter(r => kept.has(r.id)).map(r => r.id),
        refillFailed: false
      };

      const fold = (outcome: RowSaveOutcome): ListingSaveEvent => {
        store.apply(outcome);
        summary[outcome.status].push(outcome.id);
        return { kind: 'row', outcome };
      };

      const refill = defer(() => summary.saved.length
        ? this.listing.getByIds(summary.saved).pipe(
          tap(rows => store.refill(rows)),
          catchError(() => {
            summary.refillFailed = true;
            return of([]);
          }))
        : of([])
      );

      return concat(
        from(plan.invalid).pipe(map(fold)),
        from(plan.checkFailed ?? []).pipe(map(fold)),
        toSend.length ? this.batchSave.send(toSend).pipe(map(fold)) : of<never>(),
        refill.pipe(map((): ListingSaveEvent => ({ kind: 'done', summary })))
      );
    });
  }
}
